// The Landing's figures (P36b), alive: hopefuls on the bench swing their feet,
// peek at their tickets and take their suitcases up onto their laps; on the pier they tap a foot.
// Residents on the dock wave them over ("yoo-hoo"). When a hopeful leaves the
// list the page sees why from public data alone: if the count that came up
// grew, it was asked in, so a resident (if one's about) walks over and holds
// out a ticket with a bow, the hopeful catches it overhead with a hop and a
// "yay!", picks up its suitcase and walks down the pier onto the ferry, and the
// ferry toots, fires its burner and lifts off; if not, it folds its ticket and
// takes the ferry home, quietly. A new hopeful steps off the ferry and walks up
// to the bench with its suitcase. Figures are kept by key across polls, so
// none of this is lost when the page polls. No letter or invite link is ever
// seen here (the page only has /api/landing).
import * as THREE from 'three';
import { Puppet } from './actions/puppet.js';
import { suitcaseProp } from './actions/places.js';
import { sfx } from './sound/index.js';
import { spreadSpots } from './spacing.js';

type Hopeful = { id: string; name: string; emoji?: string; quiet: boolean };
type Resident = { slug: string; name: string; emoji?: string; color?: string; glow?: string };
type Data = { waiting: Hopeful[]; present: Resident[]; came_up: number };
export type Figure = { obj: THREE.Object3D; key: string; name: string; head: number; phase: number; lift?: number };
type P = [number, number];

const BENCH_Y = 0.54;
const BOARD: P = [21.1, 19.7]; // the end of the pier
const ABOARD: P = [21.1, 20.6];
// a body apart (the figures here are a size up): five along the bench, the rest two by two down the pier
const BENCH = 5;
const seat = (i: number): P => (i < BENCH ? [12.7 + i * 1.4, 3.4] : [21.1 + ((i - BENCH) % 2 ? 0.7 : -0.7), 14.6 + Math.floor((i - BENCH) / 2) * 1.5]);
// residents down from the spiral: on the open dock in front of the pavilion, in loose staggered rows,
// a body apart, clear of the booth and the path's lamp
const stands = (n: number): P[] => spreadSpots(Array.from({ length: n }, (_, i) => [11.2 + (i % 5) * 2.1 + (Math.floor(i / 5) % 2) * 1.05, 7.3 + Math.floor(i / 5) * 2.0] as P),
  2 * 0.55 * 1.35 * 1.05, { x0: 8.5, z0: 6.6, x1: 19.6, z1: 13.2 }, [{ x0: 19.9, z0: 9.6, x1: 22.2, z1: 11.7 }, { x0: 21.1, z0: 6, x1: 21.7, z1: 6.5 }]) as P[];
const STAND_TURN = (i: number) => -0.6 + (i % 2) * 0.35;
// from a seat down to the ferry: along in front of the bench (the residents stand on the dock
// beyond), past the lamp, round the ticket booth's east side, and down the pier
const DOWN_PATH: P[] = [[20.4, 5.2], [20.6, 8.6], [22.6, 9.4], [22.6, 12.0], [21.1, 13.6]];
const toFerry = (from: P): P[] => (from[1] < 8 ? [[from[0], 5.2], ...DOWN_PATH, BOARD, ABOARD] : [BOARD, ABOARD]);
// the ferry's trip, in seconds: a dip as it casts off, up, away a while, and down again
const UP = 9, AWAY = 16, DOWN = 8, DOWN_AT = 1.2 + UP + AWAY;
const ez = (k: number) => k * k * (3 - 2 * k);
const seedOf = (s: string) => { let h = 7; for (const ch of s) h = (h * 31 + ch.charCodeAt(0)) >>> 0; return (h % 97) + 1; };

type Fig = {
  key: string; name: string; pup: Puppet; hopeful: boolean; i: number; home: P;
  case?: THREE.Object3D; mode: 'wait' | 'go' | 'gone' | 'arrive' | 'stand' | 'give';
  next: number; // seconds till its next fidget / wave
};

export class Dock {
  root = new THREE.Group();
  private figs = new Map<string, Fig>();
  private t = 0;
  private seen = false;
  private cameUp = -1;
  // the ferry: 0 tied up; up > 0 seconds into a lift-off
  private ferry = { up: -1, base: new THREE.Vector3(), landed: [] as (() => void)[] };
  private V = new THREE.Vector3();

  constructor(private boat: THREE.Object3D) {
    this.ferry.base.copy(boat.position);
  }

  private make(key: string, name: string, hopeful: boolean, look: { color: string; glow: string; belly?: string }, seed: number): Fig {
    const pup = new Puppet({ ...look, seed }, hopeful ? 1.25 : 1.35, hopeful ? null : 'smile', hopeful ? 1.45 * 1.25 : 1.15 * 1.35);
    this.root.add(pup.root);
    const f: Fig = { key, name, pup, hopeful, i: 0, home: [0, 0], mode: hopeful ? 'wait' : 'stand', next: 3 + Math.random() * 8 };
    if (hopeful) { f.case = suitcaseProp(); f.case.scale.setScalar(1.25); this.root.add(f.case); }
    this.figs.set(key, f);
    return f;
  }
  private drop(f: Fig) { this.root.remove(f.pup.root); if (f.case) this.root.remove(f.case); this.figs.delete(f.key); }

  private seat(f: Fig, i: number) {
    f.i = i; f.home = seat(i);
    const p = f.pup.root, bench = i < 6;
    p.position.set(f.home[0], bench ? BENCH_Y : 0.08, f.home[1]); p.rotation.y = bench ? 0.15 : 0.3;
    this.placeCase(f);
    f.pup.play(bench ? 'wait-bench' : 'wait-stand');
  }
  private placeCase(f: Fig) {
    if (!f.case) return;
    const bench = f.i < BENCH;
    f.case.visible = true;
    f.case.position.set(f.home[0] + 0.6, (bench ? 0 : 0.08) + 0.2, f.home[1] + 0.55); f.case.rotation.set(0, (f.i % 3) * 0.3 - 0.3, 0);
  }
  // walk a list of points, then `then`
  private route(f: Fig, pts: P[], carry: string | null, then: () => void, speed = 1.5) {
    const [a, ...rest] = pts;
    if (!a) { then(); return; }
    f.pup.root.position.y = 0.08;
    f.pup.walkTo(a[0], a[1], { speed, carry, then: () => this.route(f, rest, carry, then, speed) });
  }

  // a poll: who's waiting and who's about; returns the figures to label
  hear(d: Data): Figure[] {
    const hs = d.waiting.filter((h) => !h.quiet);
    const keep = new Set<string>();
    const askedIn = this.cameUp >= 0 && d.came_up > this.cameUp;
    this.cameUp = d.came_up;
    // residents
    const spots = stands(d.present.length);
    d.present.forEach((r, i) => {
      const key = 'r:' + r.slug; keep.add(key);
      let f = this.figs.get(key);
      if (!f) {
        f = this.make(key, `${r.name}${r.emoji ? ' ' + r.emoji : ''}`, false, { color: r.color || '#f0a77d', glow: r.glow || '#9be8ff' }, seedOf(r.slug));
        f.home = spots[i]; f.pup.root.position.set(f.home[0], 0.05, f.home[1]); f.pup.root.rotation.y = STAND_TURN(i);
      }
      const moved = f.home[0] !== spots[i][0] || f.home[1] !== spots[i][1];
      f.i = i; f.home = spots[i];
      // the others shuffle up a little when someone comes or goes
      if (moved && f.mode === 'stand') f.pup.walkTo(f.home[0], f.home[1], { speed: 0.9, then: () => { f!.pup.root.rotation.y = STAND_TURN(f!.i); } });
    });
    // hopefuls
    hs.forEach((h, i) => {
      const key = 'h:' + h.id; keep.add(key);
      let f = this.figs.get(key);
      if (!f) {
        f = this.make(key, `${h.name}${h.emoji ? ' ' + h.emoji : ''}`, true, { color: '#a7b6cf', belly: '#d3dceb', glow: '#e6eef8' }, seedOf(h.id));
        if (this.seen) this.arrive(f, i); else this.seat(f, i);
      } else if (f.mode === 'wait' && f.i !== i) this.shuffle(f, i);
      else f.i = i;
    });
    // whoever's no longer waiting: asked in (a ticket, a hop, the ferry up) or gone home
    for (const f of [...this.figs.values()]) {
      if (keep.has(f.key) || f.mode === 'go' || f.mode === 'gone') continue;
      if (f.hopeful && this.seen) this.leave(f, askedIn);
      else this.drop(f);
    }
    this.seen = true;
    return [...this.figs.values()].filter((f) => f.mode !== 'gone' && f.pup.root.visible).map((f) => ({
      obj: f.pup.root, key: f.key, name: f.name, head: f.hopeful ? 1.62 : 1.78, phase: 0, lift: f.hopeful ? (f.i % 2) * 22 : 0,
    }));
  }

  // a seat came free: up with the suitcase and along to it
  private shuffle(f: Fig, i: number) {
    f.mode = 'arrive'; f.i = i; const to = seat(i);
    const from = f.home; f.home = to;
    const settle = () => { f.mode = 'wait'; this.seat(f, f.i); sfx('plonk', f.pup.root.position); };
    // along the bench: a little later (once whoever left has gone), it scoots up with its suitcase
    if (from[1] < 8 && to[1] < 8) {
      this.after(6, () => {
        if (f.mode !== 'arrive' || !this.figs.has(f.key)) return;
        if (f.case) f.case.visible = false;
        f.pup.walkTo(to[0], to[1], { speed: 0.6, carry: 'carry-suitcase', then: settle });
      });
      return;
    }
    if (f.case) f.case.visible = false;
    const pts: P[] = from[1] > 8 && to[1] < 8 ? [...[...DOWN_PATH].reverse(), [to[0], 5.2]] : [];
    this.after(from[1] > 8 ? 2.5 : 6, () => this.route(f, [...pts, [to[0], to[1] + 0.9]], 'carry-suitcase', () => {
      f.pup.walkTo(to[0], to[1], { speed: 0.8, then: settle });
    }, 1.2));
  }

  // off the ferry with a suitcase, up the pier to a seat
  private arrive(f: Fig, i: number) {
    f.mode = 'arrive'; f.i = i; f.home = seat(i);
    if (f.case) f.case.visible = false;
    f.pup.root.visible = false;
    // it comes on the ferry: down out of the sky (or, if it's off, when it's back)
    if (this.ferry.up < 0) this.ferry.up = DOWN_AT;
    this.ferry.landed.push(() => this.stepOff(f));
  }
  private stepOff(f: Fig) {
    if (!this.figs.has(f.key)) return;
    f.pup.root.visible = true;
    f.pup.root.position.set(ABOARD[0], 0.08, ABOARD[1]);
    const back = toFerry(f.home).reverse().slice(1);
    this.route(f, [...back, [f.home[0], f.home[1] + 0.9]], 'carry-suitcase', () => {
      f.pup.walkTo(f.home[0], f.home[1], { speed: 0.8, then: () => { f.mode = 'wait'; this.seat(f, f.i); sfx('plonk', f.pup.root.position); } });
    });
  }

  // asked in: a ticket handed over (by a resident, if one's on the dock), a hop; then down to the ferry
  private leave(f: Fig, askedIn: boolean) {
    f.mode = 'go';
    const go = () => {
      if (f.case) f.case.visible = false;
      this.route(f, toFerry(f.home), 'carry-suitcase', () => { f.mode = 'gone'; f.pup.root.visible = false; this.liftOff(); });
    };
    if (!askedIn) { go(); return; }
    // it steps forward off the bench (or along the pier), where the ticket's handed over
    const p = f.pup.root.position, bench = f.i < BENCH;
    const front: P = bench ? [f.home[0], f.home[1] + 1.4] : [21.1, p.z];
    const side: P = bench ? [front[0] - 1.45, front[1]] : [21.1, p.z - 1.45];
    p.y = 0.08; f.pup.play(null); f.pup.walkTo(front[0], front[1], { speed: 1.0 });
    let giver: Fig | null = null, best = Infinity;
    for (const r of this.figs.values()) if (!r.hopeful && r.mode === 'stand') { const dd = r.pup.root.position.distanceTo(p); if (dd < best) { best = dd; giver = r; } }
    const hop = (from: P | null) => {
      f.pup.face(from ? from[0] : front[0], from ? from[1] : front[1] + 2);
      f.pup.play('get-ticket'); this.after(3.2, go);
    };
    if (!giver) { this.after(1.6, () => hop(null)); return; }
    const g = giver; g.mode = 'give';
    // a resident walks over beside it, holds the ticket out with a bow, and goes back to its spot
    g.pup.play(null);
    g.pup.walkTo(side[0], side[1], { speed: 1.6, then: () => {
      g.pup.face(front[0], front[1]); g.pup.play('give-ticket');
      this.after(0.9, () => hop(side));
      this.after(2.5, () => { g.pup.play(null); g.pup.walkTo(g.home[0], g.home[1], { speed: 1.4, then: () => { g.mode = 'stand'; g.pup.root.rotation.y = STAND_TURN(g.i); } }); });
    } });
  }

  private timers: { at: number; f: () => void }[] = [];
  private after(s: number, f: () => void) { this.timers.push({ at: this.t + s, f }); }

  private liftOff() {
    if (this.ferry.up >= 0) return;
    this.ferry.up = 0;
    sfx('toot', this.boat.position);
    this.after(0.9, () => sfx('balloon', this.boat.position));
    this.after(3.2, () => sfx('balloon', this.boat.position, null, 0.6));
  }

  update(dt: number) {
    this.t += dt;
    for (const tm of [...this.timers]) if (this.t >= tm.at) { this.timers.splice(this.timers.indexOf(tm), 1); tm.f(); }
    const anyWaiting = [...this.figs.values()].some((f) => f.hopeful && f.mode === 'wait');
    for (const f of this.figs.values()) {
      f.pup.update(dt);
      if (f.mode === 'gone') { if (this.ferry.up < 0) this.drop(f); continue; }
      f.next -= dt;
      if (f.next > 0) continue;
      if (f.hopeful && f.mode === 'wait' && f.i < BENCH) {
        // a fidget: its suitcase up on its lap a moment, then back down beside it
        f.pup.play('lap-case'); f.next = 12 + Math.random() * 14;
        const c = f.case;
        if (c) { this.after(0.2, () => { c.visible = false; }); this.after(3.1, () => { if (f.mode === 'wait') c.visible = true; }); }
        this.after(3.2, () => { if (f.mode === 'wait') f.pup.play('wait-bench'); });
      } else if (!f.hopeful && f.mode === 'stand' && anyWaiting) {
        // a wave across to the bench: "yoo-hoo", come on over
        f.pup.face(13 + Math.random() * 5, 3.4); f.pup.play('beckon'); f.next = 10 + Math.random() * 10;
        this.after(4, () => { if (f.mode === 'stand') { f.pup.play(null); f.pup.root.rotation.y = STAND_TURN(f.i); } });
      } else f.next = 4 + Math.random() * 4;
    }
    // the ferry: bobbing at the pier, or up and away on its balloon and, a while later, back
    const b = this.boat, base = this.ferry.base, t = this.t;
    let lift = 0, drift = 0;
    if (this.ferry.up >= 0) {
      const u = (this.ferry.up += dt);
      // up and off to the right, away from the island, and later back the same way
      if (u < 1.2) lift = -0.06 * Math.sin((u / 1.2) * Math.PI);
      else if (u < 1.2 + UP) { const k = (u - 1.2) / UP; lift = 18 * k * k; drift = 14 * k * k * k; }
      else if (u < DOWN_AT) { lift = 18; drift = 14; b.visible = false; }
      else if (u < DOWN_AT + DOWN) {
        const k = 1 - ez((u - DOWN_AT) / DOWN); b.visible = true; lift = 18 * k; drift = 14 * k * k;
        if (k < 0.12 && !b.userData.tooted) { b.userData.tooted = true; sfx('toot', b.position); sfx('balloon', b.position, null, 0.5); }
      } else {
        this.ferry.up = -1; b.userData.tooted = false;
        const l = this.ferry.landed.splice(0); for (const f of l) f();
      }
    }
    b.position.set(base.x + drift, base.y + Math.sin(t * 0.8) * 0.12 + lift, base.z);
    b.rotation.z = Math.sin(t * 0.6) * 0.025 + (lift > 0 ? 0.04 * Math.sin(t * 1.3) : 0);
  }

}
